import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../../api/client";
import {
	Button,
	Notification,
	useNotification,
} from "../../components/FormComponents";
import TemplateCodeEditor from "../../components/TemplateCodeEditor";
import TemplateDesignEditor from "../../components/TemplateDesignEditor";
import VisualEditorLayout from "../../components/VisualEditorLayout";
import {
	DEFAULT_ACCEPTANCE_CONFIG,
	DEFAULT_PROPOSAL_THEME,
	PREVIEW_VARIABLES,
} from "../../constants/proposalDefaults";
import { TEMPLATE_CATEGORIES } from "../../constants/themes";
import { parseJsonField } from "../../utils/proposalHelpers";

// Template Builder Components
import GeneralInfo from "./TemplateBuilder/GeneralInfo";
import PreviewPane from "./TemplateBuilder/PreviewPane";
import TabContent from "./TemplateBuilder/TabContent";

export default function TemplateEditor() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const editId = searchParams.get("edit");

	const [companies, setCompanies] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [loading, setLoading] = useState(!!editId);
	const [errors, setErrors] = useState({});
	const [activeTab, setActiveTab] = useState("content");
	const [collapsedSections, setCollapsedSections] = useState({});
	const { notification, showNotification } = useNotification();
	const [isPreviewingCode, setIsPreviewingCode] = useState(false);

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		company_id: "",
		category: "full",
		sections: [],
		hero_cards: [],
		default_items: [],
		template_code: "",
		theme: { ...DEFAULT_PROPOSAL_THEME },
		variables: { ...PREVIEW_VARIABLES },
		acceptance_config: { ...DEFAULT_ACCEPTANCE_CONFIG },
		code_type: "react",
	});
	const [isDirty, setIsDirty] = useState(false);
	const skipDirtyRef = useRef(editId ? 2 : 1);

	// Warn on unsaved changes
	useEffect(() => {
		const handler = (e) => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [isDirty]);

	// Track dirty state after initial load
	useEffect(() => {
		if (skipDirtyRef.current > 0) {
			skipDirtyRef.current--;
			return;
		}
		setIsDirty(true);
	}, []);

	// Reset code preview when category changes
	useEffect(() => {
		if (formData.category !== "full") {
			setIsPreviewingCode(false);
		}
	}, [formData.category]);

	const fetchInitialData = useCallback(async () => {
		try {
			const companiesRes = await apiClient.get("/companies/all");
			setCompanies(companiesRes.data);

			if (editId) {
				setLoading(true);
				const res = await apiClient.get(`/templates/${editId}`);
				const tmpl = res.data;
				const tmplData = parseJsonField(tmpl.template_data, {});
				setFormData({
					name: tmpl.name || "",
					description: tmpl.description || "",
					company_id: tmpl.company_id || "",
					category: tmpl.category || "full",
					sections: parseJsonField(tmpl.sections, []),
					hero_cards: parseJsonField(tmpl.hero_cards, []),
					default_items: parseJsonField(tmpl.default_items, []),
					template_code: tmpl.template_code || "",
					theme: tmplData.theme || { ...DEFAULT_PROPOSAL_THEME },
					variables: tmplData.variables || { ...PREVIEW_VARIABLES },
					acceptance_config: tmpl.acceptance_config || {
						...DEFAULT_ACCEPTANCE_CONFIG,
					},
					code_type: tmplData.code_type || "react",
				});
				setLoading(false);
			}
		} catch (error) {
			console.error("Error loading data:", error);
			showNotification("Erro ao carregar dados", "error");
			setLoading(false);
		}
	}, [editId, showNotification]);

	useEffect(() => {
		fetchInitialData();
	}, [fetchInitialData]);

	const handleSubmit = async () => {
		if (!formData.name.trim()) {
			setErrors({ name: "Nome obrigatorio" });
			return;
		}

		try {
			setIsSaving(true);
			const { theme, variables, acceptance_config, code_type, ...rest } =
				formData;
			const payload = {
				...rest,
				template_data: { theme, variables, code_type: code_type || "react" },
				acceptance_config,
				sections: formData.sections.filter(
					(s) =>
						s.type === "company_intro" ||
						s.title ||
						s.content ||
						(s.cards && s.cards.length > 0) ||
						(s.images && s.images.length > 0),
				),
			};

			if (editId) {
				await apiClient.put(`/templates/${editId}`, payload);
				showNotification("Template atualizado com sucesso!");
			} else {
				await apiClient.post("/templates", payload);
				showNotification("Template criado com sucesso!");
			}
			setIsDirty(false);

			setTimeout(() => navigate("/admin/templates"), 500);
		} catch (error) {
			console.error("Error saving template:", error);
			showNotification(
				error.response?.data?.error || "Erro ao salvar",
				"error",
			);
		} finally {
			setIsSaving(false);
		}
	};

	// Company for preview
	const selectedCompany = companies.find(
		(c) => String(c.id) === String(formData.company_id),
	);
	const previewCompany = {
		name: selectedCompany?.name || "Empresa",
		tagline: selectedCompany?.tagline || "",
		description: selectedCompany?.description || "",
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
			</div>
		);
	}

	// Editor pane content
	const editorPane = (
		<div className="space-y-5">
			<GeneralInfo
				formData={formData}
				setFormData={setFormData}
				errors={errors}
				setErrors={setErrors}
				companies={companies}
			/>

			{/* Tabs Navigation */}
			<div className="flex items-center gap-1 p-1 bg-slate-950/50 rounded-lg border border-white/5">
				{[
					"content",
					"design",
					...(formData.category === "full" ? ["code"] : []),
				].map((tab) => (
					<button
						key={`tab-${tab}`}
						type="button"
						onClick={() => setActiveTab(tab)}
						className={`flex-1 flex items-center justify-center gap-2 py-2 text-[11px] font-bold uppercase tracking-wider rounded-md transition-all ${
							activeTab === tab
								? "bg-indigo-600/20 text-indigo-400 border border-indigo-500/20 shadow-lg"
								: "text-slate-500 hover:text-slate-300"
						}`}
					>
						{tab === "content"
							? "Conteúdo"
							: tab === "design"
								? "Design"
								: "Código"}
					</button>
				))}
			</div>

			{activeTab === "content" && (
				<TabContent
					formData={formData}
					setFormData={setFormData}
					showNotification={showNotification}
					collapsedSections={collapsedSections}
					setCollapsedSections={setCollapsedSections}
				/>
			)}

			{activeTab === "design" && (
				<TemplateDesignEditor
					theme={formData.theme}
					variables={formData.variables}
					acceptanceConfig={formData.acceptance_config}
					onAcceptanceChange={(acceptance_config) =>
						setFormData({ ...formData, acceptance_config })
					}
					onChange={(variables, theme) =>
						setFormData({ ...formData, variables, theme })
					}
				/>
			)}

			{activeTab === "code" && formData.category === "full" && (
				<div className="animate-in fade-in slide-in-from-bottom-1 duration-300">
					<TemplateCodeEditor
						value={formData.template_code}
						onChange={(code) =>
							setFormData({ ...formData, template_code: code })
						}
						templateName={formData.name}
						onPreview={() => setIsPreviewingCode(true)}
						codeType={formData.code_type || "react"}
						onCodeTypeChange={(type) =>
							setFormData({ ...formData, code_type: type })
						}
					/>
				</div>
			)}
		</div>
	);

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<button
						type="button"
						onClick={() => navigate("/admin/templates")}
						className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-all"
						aria-label="Voltar para templates"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<div>
						<h1 className="text-2xl font-bold text-white">
							{editId ? "Editar Template" : "Novo Template"}
						</h1>
						<p className="text-xs text-slate-500">
							Templates &gt; {editId ? "Editar" : "Novo"}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => navigate("/admin/templates")}
						className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-all"
					>
						Cancelar
					</button>
					<Button type="button" onClick={handleSubmit} disabled={isSaving}>
						{isSaving ? (
							<Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
						) : (
							<Save className="w-4 h-4 inline mr-2" />
						)}
						{editId ? "Atualizar" : "Criar"}
					</Button>
				</div>
			</div>

			{/* Split-pane editor */}
			<VisualEditorLayout
				editor={editorPane}
				preview={
					<PreviewPane
						formData={formData}
						isPreviewingCode={isPreviewingCode}
						setIsPreviewingCode={setIsPreviewingCode}
						previewCompany={previewCompany}
					/>
				}
				previewTitle={`Preview - ${TEMPLATE_CATEGORIES.find((c) => c.value === formData.category)?.label || "Template"}`}
			/>
		</div>
	);
}
